import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import DoctorLayout from "@/components/DoctorLayout";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Appointments from "@/pages/Appointments";
import Medicines from "@/pages/Medicines";
import Ipd from "@/pages/Ipd";
import Queue from "@/pages/Queue";
import Superbills from "@/pages/Superbills";
import QueueDisplay from "@/pages/QueueDisplay";
import Compliance from "@/pages/Compliance";
import Reports from "@/pages/Reports";
import Pharmacy from "@/pages/Pharmacy";
import Team from "@/pages/Team";
import Finance from "@/pages/Finance";
import Patients from "@/pages/Patients";
import PatientDetail from "@/pages/PatientDetail";
import NewVisit from "@/pages/NewVisit";
import VisitView from "@/pages/VisitView";
import PortalLogin from "@/pages/portal/PortalLogin";
import PortalDashboard from "@/pages/portal/PortalDashboard";
import { Loader2 } from "lucide-react";

function FullPageLoader() {
    return (
        <div className="min-h-screen flex items-center justify-center bg-[#F8FAF9]" data-testid="app-loading">
            <Loader2 className="h-8 w-8 animate-spin text-[#E06D1F]" />
        </div>
    );
}

function Protected({ children }) {
    const { user, loading } = useAuth();
    if (loading) return <FullPageLoader />;
    if (!user) return <Navigate to="/login" replace />;
    return children;
}

function GuestOnly({ children }) {
    const { user, loading } = useAuth();
    if (loading) return <FullPageLoader />;
    if (user) return <Navigate to="/" replace />;
    return children;
}

function App() {
    return (
        <div className="App">
            <AuthProvider>
                <BrowserRouter>
                    <Routes>
                        <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
                        <Route path="/register" element={<GuestOnly><Register /></GuestOnly>} />
                        <Route path="/forgot-password" element={<GuestOnly><ForgotPassword /></GuestOnly>} />
                        <Route path="/reset-password" element={<GuestOnly><ResetPassword /></GuestOnly>} />
                        <Route path="/portal" element={<PortalLogin />} />
                        <Route path="/portal/dashboard" element={<PortalDashboard />} />
                        <Route path="/queue/display" element={<Protected><QueueDisplay /></Protected>} />
                        <Route element={<Protected><DoctorLayout /></Protected>}>
                            <Route path="/" element={<Dashboard />} />
                            <Route path="/patients" element={<Patients />} />
                            <Route path="/appointments" element={<Appointments />} />
                            <Route path="/medicines" element={<Medicines />} />
                            <Route path="/ipd" element={<Ipd />} />
                            <Route path="/queue" element={<Queue />} />
                            <Route path="/compliance" element={<Compliance />} />
                            <Route path="/superbills" element={<Superbills />} />
                            <Route path="/reports" element={<Reports />} />
                            <Route path="/pharmacy" element={<Pharmacy />} />
                            <Route path="/team" element={<Team />} />
                            <Route path="/finance" element={<Finance />} />
                            <Route path="/patients/:id" element={<PatientDetail />} />
                            <Route path="/patients/:id/visits/new" element={<NewVisit />} />
                            <Route path="/visits/:id" element={<VisitView />} />
                        </Route>
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </BrowserRouter>
                <Toaster position="bottom-right" richColors />
            </AuthProvider>
        </div>
    );
}

export default App;
